import { cloneDeep } from 'lodash';
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { useData } from '@/components';
import { sortVariantByPrice } from '@/components/Counter';
import { calculateTotalGrams } from '@/components/Counter/Utils';
import { useNotify, useProducts, useSummary } from '@/hooks';
import { GProduct, Variant } from '@/types';

import { getCompoundDisplayValue } from 'utils/utils';
import {
  Compound,
  CompoundType,
  ProductContext,
  UnitPricingTier,
} from './ProductContext';

export interface ISelectOption<T> {
  label: string;
  value: T;
}
interface ProductProviderProps {
  product: GProduct;
  itShowDetailAvg?: boolean;
}

const ProductProvider: FC<PropsWithChildren<ProductProviderProps>> = ({
  product,
  children,
  itShowDetailAvg = false,
}) => {
  const { productCard } = useData();
  const cannabinoidDisplayOrder =
    productCard?.cannabinoid_display_order ?? 'THC CBD TAC';
  const sortedWeightVariants = sortVariantByPrice(
    product.variants as Variant[],
  );
  const firstWeightVariant = sortedWeightVariants[0];

  const { notify } = useNotify();
  const { gramsLimit } = useSummary();
  const { products, update } = useProducts();

  const [weightVariant, setWeightVariant] =
    useState<Variant>(firstWeightVariant);
  const [isSpecial, setIsSpecial] = useState(
    firstWeightVariant?.salePrice! < firstWeightVariant?.price!,
  );
  const [quantity, setQuantity] = useState(0);

  const GRAM_LIMIT_EXCEEDED_ERROR = `It is not allowed to buy more than ${gramsLimit} grams`;

  const unitPricingTiers = useMemo(
    () =>
      product.pricing?.tier_pricing_detail?.map(
        ({ start_value, price_per_value }) => ({
          qty: start_value,
          price: price_per_value,
        }),
      ) ?? [],
    [product],
  );

  const compoundList = useMemo(
    () =>
      cannabinoidDisplayOrder?.split(' ')?.reduce((acc, compound: Compound) => {
        const value = getCompoundDisplayValue(
          compound,
          product,
          itShowDetailAvg,
        );
        if (value === null) return acc;

        return [...acc, { type: compound, value }];
      }, [] as CompoundType[]),
    [cannabinoidDisplayOrder, product, itShowDetailAvg],
  );

  const isUnitPricing = product.pricing?.tier_method === 'UNIT';
  const [unitPricingTier, setUnitPricingTier] = useState<
    UnitPricingTier | undefined
  >(isUnitPricing ? unitPricingTiers?.[0] : undefined);

  useEffect(() => {
    const prod = products.find(
      p => p.id === product.id && weightVariant?.id === p?.weightVariant?.id,
    );
    setQuantity(prod?.quantity ?? 0);
  }, [weightVariant]);

  useEffect(() => {
    const p = products.find(p => p.id === product.id);
    let wv: Variant;

    // If the product is already on the cart show the
    // selected variant
    if (p !== undefined) {
      wv = p.weightVariant;
      setWeightVariant(p.weightVariant);
      setQuantity(p.quantity);
    } else {
      const sortedWeightVariants = sortVariantByPrice(
        product?.variants as Variant[],
      );
      wv = sortedWeightVariants[0];
      setWeightVariant(wv);
      setQuantity(0);
    }
    if (p?.pricing?.tier_method !== 'UNIT') {
      setIsSpecial(wv?.salePrice! < wv?.price!);
    }
  }, [products, product.id, product.variants]);

  const updateWeightVariant = (
    v: ISelectOption<string>,
    ignoreGrmLimit: boolean = true,
  ) => {
    const newWeightVariant = product.variants?.find(p => p?.id == v.value);

    // save the product on the
    // persisted store
    const newProducts = cloneDeep(products);

    const index = products.findIndex(p => p.id === product.id);

    setWeightVariant(newWeightVariant as Variant);

    if (index === -1) return;

    if (newProducts[index]) {
      newProducts[index].weightVariant = newWeightVariant as Variant;
    }

    if (ignoreGrmLimit) return;

    if (calculateTotalGrams(newProducts) > gramsLimit) {
      notify('error', GRAM_LIMIT_EXCEEDED_ERROR);
      return;
    }
  };

  const updateUnitPricingTier = (option: UnitPricingTier) => {
    setUnitPricingTier(option);
    const newProducts = cloneDeep(products);
    const index = products.findIndex(p => p.id === product.id);
    if (index === -1) return;
    if (newProducts[index]) {
      newProducts[index].quantity = option.qty;
    }
    setQuantity(option.qty);
    update(newProducts);
  };

  useEffect(() => {
    if (!isUnitPricing) {
      setUnitPricingTier(undefined);
      return;
    }
    if (!quantity) {
      setUnitPricingTier(unitPricingTiers?.[0]);
      return;
    }
    const selectedTier =
      unitPricingTiers?.find(tier => tier.qty === quantity) ??
      unitPricingTiers?.at(-1);
    setUnitPricingTier(selectedTier);
    const newProducts = cloneDeep(products);
    const index = products.findIndex(p => p.id === product.id);
    if (index === -1) return;
    if (newProducts[index]) {
      newProducts[index].unitPricingTier = selectedTier;
    }
    update(newProducts);
  }, [quantity, isUnitPricing]);

  useEffect(() => {
    if (!unitPricingTier) return;

    setIsSpecial((unitPricingTier?.price || 0) * 100 < weightVariant?.price!);
  }, [unitPricingTier]);

  return (
    <ProductContext
      value={{
        product,
        isSpecial,
        quantity,
        updateWeightVariant,
        variants: sortedWeightVariants,
        weightVariant,
        unitPricingTiers,
        unitPricingTier,
        updateUnitPricingTier,
        compoundList,
      }}
    >
      {children}
    </ProductContext>
  );
};

export { ProductProvider };
